import { Fragment, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { Select } from '@components/select';
import { SelectMulti, SelectOption } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';
import { fileNameFromPath } from '@utils/strings';

import { api } from 'api';

import { Box } from 'components/Box';
import { FileUpload } from 'components/FileUpload';
import { FileUploadAnother } from 'components/FileUploadAnother';
import { Loading } from 'components/Loading';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { Attachment, SubmitError } from 'types/applications';
import { JobQuestion } from 'types/jobs';

import { prepData } from './utils';

type Props = ModalProps<{
  applicationId: number;
}>;

function EditApplicantModal(props: Props) {
  const { applicationStore, t } = useStore();
  const { applicationId, closeModal } = props;
  const [jobId, setJobId] = useState<number | null>(null);
  const [language, setLanguage] = useState<string | undefined>(undefined);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const [ssn, setSsn] = useState<string | undefined>(undefined);
  const [cv, setCv] = useState<File | null>(null);
  const [coverLetter, setCoverLetter] = useState<File | null>(null);
  const [files, setFiles] = useState<Record<string, Attachment | File | Array<Attachment | File>>>(
    {}
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [questions, setQuestions] = useState<JobQuestion[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const setFile = (
    questionId: string,
    file: Attachment | File,
    multiple: boolean = false,
    multipleIndex: number | null = null
  ) => {
    setFiles((old) => {
      if (multiple) {
        let filesArray: Array<Attachment | File> = Object.assign([], old[questionId]);
        if (multipleIndex) {
          filesArray.splice(multipleIndex, 1, file);
        } else if (file) {
          filesArray.push(file);
        }
        return { ...old, [questionId]: filesArray };
      }
      if (old[questionId] === undefined) {
        return { ...old, [questionId]: file };
      }
      const newObj = { ...old };
      delete newObj[questionId];
      return newObj;
    });
  };

  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => api.applications.loadFromDb(applicationId),
    onSuccess: (data) => {
      setJobId(data.job);
      setName(data.name);
      setEmail(data.email);
      setQuestions(data.form.sections[0].questions);
      setAttachments(data.attachments);
      data.form.sections[0].questions.forEach((question) => {
        if (
          question.type === 'attachment' &&
          question.multiple &&
          question.value &&
          Array.isArray(question.value) &&
          question.value.length > 0
        ) {
          question.value.forEach((value, index) => {
            const attachment = data.attachments.find((a) => a.id.toString() === value.toString());
            if (attachment) {
              setFile(question.id, attachment, true, index);
            }
          });
        }
      });
    },
  });

  const jobQuery = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.jobs.loadFromDbUnhandled(jobId!),
    enabled: jobId !== null,
  });

  const updateApplication = () => {
    const data = {
      language,
      name,
      email,
      phone,
      ssn,
      files,
      cv,
      coverLetter,
      sections: [
        {
          id: job?.form.sections[0].id!,
          questions: questions,
        },
      ],
    };
    const formData = prepData(data);
    return api.applications.edit(applicationId, formData);
  };
  const queryClient = useQueryClient();

  const submit = useMutation<{}, SubmitError>(updateApplication, {
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries(['applicants']), 2000);
      applicationStore.load(applicationId); // Applicant detail isn't using react-query, remove when it does
      closeModal();
    },
    onError: (error) => {
      setErrors(error.response?.data || {});
    },
  });

  if (!applicationQuery.isSuccess || !jobQuery.isSuccess) {
    return <Loading />;
  }
  const application = applicationQuery.data;
  const job = jobQuery.data;

  function findOptionsFromJobForm(questionId: string) {
    const jobFormQuestion = job.form.sections[0].questions.find((q) => q.id === questionId);
    return jobFormQuestion?.options.map((option) => ({ ...option, label: option.display })) || [];
  }

  function findOptionsFromApplicationForm(options: Array<{ value: string; display: string }>) {
    return options && options.map((option) => ({ ...option, label: option.display }));
  }

  function getQuestionField(q: JobQuestion) {
    const onChange = (value: string | string[]) => {
      const newQuestions = questions.map((question) => {
        if (question.id === q.id) {
          question.value = value;
        }
        return question;
      });
      setQuestions(newQuestions);
    };
    let el = null;

    switch (q.type) {
      case 'attachment':
        if (q.multiple) {
          const questionFiles = files[q.id];
          el = (
            <Stack space={1}>
              <FormLabel text={q.title} />
              {(!questionFiles || (Array.isArray(questionFiles) && questionFiles.length === 0)) && (
                <FileUpload
                  key={q.id}
                  // @ts-ignore
                  file={null}
                  // @ts-ignore
                  onChange={(file) => setFile(q.id, file, true)}
                />
              )}
              {questionFiles && Array.isArray(questionFiles) && (
                <Stack space={2}>
                  {questionFiles.map((v, index) => {
                    const attachment = attachments.find(
                      (a) => a.questionId === index + '__' + q.id
                    );
                    return (
                      <FileUpload
                        // @ts-ignore
                        file={v || attachment}
                        // @ts-ignore
                        onChange={(file) => setFile(q.id, file, true, index)}
                      />
                    );
                  })}
                  <FileUploadAnother
                    key={q.id}
                    // @ts-ignore
                    onChange={(file) => setFile(q.id, file, true)}
                  />
                </Stack>
              )}
            </Stack>
          );
        } else {
          const attachment = attachments.find((a) => a.questionId === q.id);
          el = (
            <Stack space={1}>
              <FormLabel text={q.title} />
              <FileUpload
                key={q.id}
                // @ts-ignore
                file={files[q.id] || attachment || null}
                // @ts-ignore
                onChange={(file) => setFile(q.id, file)}
              />
            </Stack>
          );
        }
        break;
      case 'multidropdown':
        const options = findOptionsFromApplicationForm(q.options) || findOptionsFromJobForm(q.id);
        let selectedOptions: SelectOption[] = [];
        if (q.value && Array.isArray(q.value)) {
          options.forEach((option) => {
            if (q.value?.includes(option.value)) {
              selectedOptions.push(option);
            }
          });
        }
        el = (
          <Stack space={1}>
            <SelectMulti
              placeholder={t('common.forms.select.placeholder')}
              noOptions={() => null}
              defaultValue={selectedOptions}
              options={options}
              onChange={(options) => onChange(options.map((option) => option.value))}
              label={q.title}
            />
          </Stack>
        );
        break;
      case 'dropdown': {
        const options = findOptionsFromApplicationForm(q.options) || findOptionsFromJobForm(q.id);
        el = (
          <Select
            label={q.title}
            defaultValue={Array.isArray(q.value) ? q.value[0] : q.value}
            options={options}
            onChange={onChange}
          />
        );
        break;
      }
      case 'multi_line':
        el = <TextArea label={q.title} defaultValue={q.value || ''} onChange={onChange} />;
        break;
      case 'one_line':
      default:
        el = (
          <TextField
            label={q.title}
            name={q.title}
            defaultValue={Array.isArray(q.value) ? q.value[0] : q.value}
            onChange={onChange}
          />
        );
        break;
    }
    return <div key={q.id}>{el}</div>;
  }

  return (
    <Modal key={application.language}>
      <Modal.Header text={t('application.edit.header')} />
      <Stack space={4}>
        {job.languages.filter((language) => language.isEnabled).length > 1 && (
          <Box mb="12">
            <Select
              label={t('job.create.form.label.language')}
              isClearable={false}
              isSearchable={false}
              defaultValue={application.language}
              options={job.languages
                .filter((language) => language.isEnabled)
                .map((obj) => ({
                  label: t(`general.${obj.language}`),
                  value: obj.language,
                }))}
              onChange={setLanguage}
            />
          </Box>
        )}
        <TextField
          label={'* ' + t('job.create.form.label.name')}
          defaultValue={application.name || ''}
          onChange={setName}
          error={errors.name}
        />
        <TextField
          label={'* ' + t('job.create.form.label.email')}
          defaultValue={application.email || ''}
          onChange={setEmail}
          error={errors.email}
        />
        {job.includePhone && (
          <TextField
            label={t('job.create.form.label.phone')}
            defaultValue={application.phone || ''}
            onChange={setPhone}
          />
        )}
        {job.includeSsn && (
          <TextField
            label={t('job.create.form.label.ssn')}
            defaultValue={application.ssn || ''}
            onChange={setSsn}
          />
        )}
        <Stack space={1}>
          <FormLabel text={t('modals.addapplicant.field.cv')} />
          <FileUpload
            // @ts-ignore
            file={
              cv ||
              (applicationQuery.data.cvOriginal
                ? { name: fileNameFromPath(applicationQuery.data.cvOriginal) }
                : null)
            }
            onChange={setCv}
          />
        </Stack>

        <Stack space={1}>
          <FormLabel text={t('modals.addapplicant.field.cover')} />
          <FileUpload
            // @ts-ignore
            file={
              coverLetter ||
              (applicationQuery.data.coverLetterOriginal
                ? { name: fileNameFromPath(applicationQuery.data.coverLetterOriginal) }
                : null)
            }
            onChange={setCoverLetter}
          />
        </Stack>
      </Stack>
      {application.form.sections[0].questions.length > 0 &&
        application.form.sections.map((f) => {
          return (
            <Fragment key={f.id}>
              <Separator />
              <Stack space={4}>{f.questions.map(getQuestionField)}</Stack>
            </Fragment>
          );
        })}
      <Modal.Actions>
        <Button
          variant="secondary"
          isDisabled={submit.isLoading}
          onClick={closeModal}
          text={t('modals.addapplicant.button.cancel')}
        />
        <Button
          isDisabled={submit.isLoading}
          onClick={() => {
            let hasErrors = false;
            if (!name) {
              setErrors((prev) => {
                return { ...prev, name: t('errors.field.required') };
              });
              hasErrors = true;
            }
            if (!email) {
              setErrors((prev) => {
                return { ...prev, email: t('errors.field.required') };
              });
              hasErrors = true;
            }
            if (hasErrors) {
              return;
            }
            submit.mutate();
          }}
          text={t('modals.addapplicant.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default EditApplicantModal;
